import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from 'src/app/api.service';
import { ScormPlayerSessionService } from 'src/app/service/scorm-events/scorm-player-session.service';
import { RoleplayMockService } from './roleplay-mock.service';
import {
  isMediaTurn,
  RoleplayEndResponseDto,
  RoleplayFramesResponseDto,
  RoleplayIds,
  RoleplayMessage,
  RoleplayMineAllFiltros,
  RoleplayMineAllResponseDto,
  RoleplayMode,
  RoleplayRealtimeRole,
  RoleplayRealtimeTokenDto,
  RoleplayRealtimeTurnResponseDto,
  RoleplayResult,
  RoleplaySession,
  RoleplaySessionResult,
  RoleplaySessionResultsDto,
  RoleplayStartResponseDto,
  RoleplayTopicConfig,
  RoleplayTopicResponseDto,
  RoleplayTranscriptTurnDto,
  RoleplayTurnPayload,
  RoleplayTurnResponse,
  RoleplayTurnResponseDto,
  TopicRoleplayDto,
} from '../models/roleplay.model';

/**
 * Fachada única que consume la página de evaluación práctica.
 *
 * Hace dos cosas y solo dos:
 *
 * 1. Elige de dónde salen los datos: `RoleplayMockService` o los endpoints de
 *    `ApiService`. Ambos devuelven los MISMOS DTOs, así que es un booleano.
 * 2. Traduce esos DTOs al modelo que pintan la página y los componentes.
 */
@Injectable({ providedIn: 'root' })
export class RoleplayService {
  /**
   * ⚠️ Poner en false cuando existan los endpoints reales.
   * Es el interruptor del roleplay **por turnos**: manda sobre todas las
   * llamadas de las modalidades chat, voz y video.
   *
   * No alcanza a `streaming`: ver `usarMock()`.
   */
  private readonly USE_MOCK = false;

  /**
   * ⚠️ TODO(api): hardcodeado mientras backend no lo mande. Identifica al
   * cliente, no a la sesión. El escenario ya sale del tema
   * (`tema.roleplay.scenario.id`).
   */
  private readonly CUSTOMER_ID = '68';

  constructor(
    private mock: RoleplayMockService,
    private api: ApiService,
    private scormSession: ScormPlayerSessionService
  ) {}

  // ── El roleplay dentro del tema ────────────────────────────────────────────
  //
  // Funciones puras sobre el payload de `temas/v2`: no piden nada, no dependen
  // de `USE_MOCK`. Son el ÚNICO punto a tocar si backend cambia el contrato del
  // bloque `roleplay`.

  /**
   * El bloque `roleplay` del tema, o null si no tiene.
   *
   * La validación es doble a propósito: backend manda el objeto en null cuando
   * no hay roleplay, así que `has` es redundante hoy, pero se comprueba igual.
   */
  roleplayDeTema(tema: any): TopicRoleplayDto | null {
    return tema?.roleplay && tema.roleplay.has ? tema.roleplay : null;
  }

  /**
   * Intentos que le quedan al usuario.
   *
   * Devuelve 0 —y por lo tanto bloquea— cuando `max_attempts` viene null o 0:
   * que no haya intentos configurados no significa poder darla siempre, es el
   * mismo criterio que la teórica (`intentos_restantes > 0`).
   */
  attemptsLeft(rp: TopicRoleplayDto | null): number {
    if (!rp) return 0;
    return Math.max(0, (rp.max_attempts ?? 0) - (rp.attempts ?? 0));
  }

  /** Puede entrar: le queda al menos un intento. */
  hasAttemptsLeft(rp: TopicRoleplayDto | null): boolean {
    return this.attemptsLeft(rp) > 0;
  }

  /**
   * Aprobado y con la repetición desactivada por configuración del espacio: no
   * se puede volver a dar **aunque le queden intentos**.
   *
   * Se compara con `=== false` y no con `!`: si la llave no llega (payload
   * viejo, o el listado que todavía no la manda) se asume permitido, que es el
   * comportamiento de siempre.
   */
  cerradoPorAprobacion(rp: TopicRoleplayDto | null): boolean {
    return !!rp && rp.passed === true && rp.allow_practice_after_pass === false;
  }

  /** Le quedan intentos y la configuración le deja usarlos. */
  puedeIntentar(rp: TopicRoleplayDto | null): boolean {
    return this.hasAttemptsLeft(rp) && !this.cerradoPorAprobacion(rp);
  }

  /**
   * Ya rindió el roleplay: gastó al menos un intento.
   *
   * Es el sustituto del `estado: 'realizado'` que no existe en el contrato. Con
   * `is_evaluable: false` es lo único que hay (nunca llega `passed`), y con
   * `is_evaluable: true` `passed` dice además si aprobó.
   */
  isDone(rp: TopicRoleplayDto | null): boolean {
    return !!rp && (rp.attempts ?? 0) > 0;
  }

  /** El front sabe correr esta modalidad. */
  modoSoportado(rp: TopicRoleplayDto | null): boolean {
    return !!rp && ['chat', 'voice', 'video', 'streaming'].includes(rp.student_mode);
  }

  /**
   * Configuración del roleplay del tema: escenario y personaje.
   *
   * Va por su propio endpoint (`GET roleplay/topics/{topic_id}`) y la página la
   * pide **en paralelo** a la sesión: mientras no llegue, la cabecera y el autor
   * de las burbujas muestran skeleton, pero la conversación abre igual.
   *
   * @param modo solo lo usa el mock, que necesita saber qué guion devolver.
   */
  async getTopicConfig(topicId: string | number, modo: RoleplayMode): Promise<RoleplayTopicConfig> {
    const dto = this.usarMock(modo)
      ? await this.mock.obtenerTopic(topicId, modo)
      : await firstValueFrom(this.api.obtenerTopicRoleplay(topicId));

    return this.aTopicConfig(dto);
  }

  /**
   * Abre la sesión y devuelve el primer turno del personaje.
   *
   * El tema es lo único que la API real necesita: viaja en la URL
   * (`roleplay/topics/{topic_id}/sessions`) y el body va vacío. El mock sigue
   * pidiendo `scenario_id` + `mode` porque es lo que necesita para elegir guion;
   * el escenario real sale del tema (`tema.roleplay.scenario.id`) y solo llega
   * hasta aquí si la página ya lo tiene a mano.
   */
  async startSession(
    modo: RoleplayMode,
    topicId: string | number,
    scenarioId?: string
  ): Promise<RoleplaySession> {
    const dto = this.usarMock(modo)
      ? await this.mock.iniciarSesion({
          ...this.ids,
          scenario_id: scenarioId ?? `mock-scenario-${modo}`,
          mode: modo,
        })
      : await firstValueFrom(this.api.iniciarSesionRoleplay(topicId));

    return this.aSesion(dto, modo);
  }

  /** Envía la respuesta del usuario (texto o media) y devuelve el turno siguiente. */
  async sendTurn(
    sessionId: string,
    payload: RoleplayTurnPayload,
    modo: RoleplayMode
  ): Promise<RoleplayTurnResponse> {
    const esMedia = isMediaTurn(payload);

    let dto: RoleplayTurnResponseDto;

    if (esMedia) {
      // Siempre audio, también en video: el turno es lo que el usuario **dijo**.
      // La imagen de la cámara no viaja acá, sino por su propio canal
      // (`sendFrames()` → `/frames`), que es lo que backend analiza para las
      // expresiones.
      const fileName = `roleplay_${Date.now()}.${this.extensionDe(payload.mimeType)}`;

      dto = this.usarMock(modo)
        ? await this.mock.enviarTurnoMedia(sessionId, payload.blob, fileName)
        : await firstValueFrom(
            this.api.enviarTurnoMediaRoleplay(sessionId, payload.blob, fileName)
          );
    } else {
      const body = { text: payload.texto };
      dto = this.usarMock(modo)
        ? await this.mock.enviarTurnoTexto(sessionId, body)
        : await firstValueFrom(this.api.enviarTurnoTextoRoleplay(sessionId, body));
    }

    return this.aTurno(dto, esMedia);
  }

  /**
   * Envía muestras de la cámara para el análisis de expresiones. Solo la
   * modalidad video las produce, y no sustituyen al turno: van por su propio
   * endpoint, en paralelo a la conversación.
   */
  async sendFrames(sessionId: string, frames: string[]): Promise<RoleplayFramesResponseDto> {
    return this.USE_MOCK
      ? this.mock.enviarFrames(sessionId, frames)
      : firstValueFrom(this.api.enviarFramesRoleplay(sessionId, frames));
  }

  /** Cierra la sesión y devuelve el resultado de la evaluación. */
  async finishSession(sessionId: string, modo: RoleplayMode): Promise<RoleplayResult> {
    const dto = this.usarMock(modo)
      ? await this.mock.finalizarSesion(sessionId, this.ids)
      : await firstValueFrom(this.api.finalizarSesionRoleplay(sessionId));

    return this.aResultado(dto);
  }

  // ── el roleplay como actividad propia (vista "Roleplays") ──────────────────
  //
  // Segunda entrada al MISMO roleplay. Lo que devuelven estos métodos son los
  // mismos modelos del flujo del tema, y la página de la conversación no
  // distingue por dónde entró.
  //
  // Un item del grupo `topic` NO usa estos métodos para practicar: como trae su
  // `topic_id`, va por los del tema (`getTopicConfig()` / `startSession()`), que
  // es el camino que backend ya tiene probado y el que descuenta el intento y
  // actualiza la nota. Los `…Libre()` son para el grupo `free`, que no cuelga
  // de ningún tema.

  /**
   * Roleplays asignados al usuario, agrupados y con sus totales.
   *
   * Devuelve la respuesta tal cual: los dos grupos ya vienen separados y con su
   * `label`, y todo lo que la tarjeta necesita (`status`, `can_practice`,
   * `attempts_remaining`…) llega calculado. No hay nada que mapear ni derivar.
   *
   * Los filtros van al servidor: cada chip del listado vuelve a llamar con los
   * suyos. `counts`, en cambio, se calcula siempre sobre el listado completo.
   */
  async listarRoleplays(filtros: RoleplayMineAllFiltros = {}): Promise<RoleplayMineAllResponseDto> {
    return firstValueFrom(this.api.listarRoleplays(filtros));
  }

  /**
   * Configuración del escenario de un roleplay `free`, para la cabecera de la
   * conversación (`GET roleplay/free/{id}/config`).
   */
  async getRoleplayLibreConfig(
    roleplayId: string | number,
    modo: RoleplayMode
  ): Promise<RoleplayTopicConfig> {
    const dto = this.usarMock(modo)
      ? await this.mock.obtenerRoleplayLibre(roleplayId, modo)
      : await firstValueFrom(this.api.obtenerRoleplayLibre(roleplayId));

    return this.aTopicConfig(dto);
  }

  /**
   * Abre la sesión de un roleplay `free` (`POST roleplay/free/{id}/sessions`).
   *
   * El id es el del item del listado, no un tema: los `topic` abiertos desde esa
   * misma vista **no** pasan por acá, van por `startSession()`.
   */
  async startSessionLibre(
    roleplayId: string | number,
    modo: RoleplayMode
  ): Promise<RoleplaySession> {
    const dto = this.usarMock(modo)
      ? await this.mock.iniciarSesionLibre(roleplayId, modo)
      : await firstValueFrom(this.api.iniciarSesionRoleplayLibre(roleplayId));

    return this.aSesion(dto, modo);
  }

  /**
   * Resultado guardado de una sesión concreta, por `session_id`.
   *
   * Trae los datos que `/end` no manda: el bloque `evaluation` del intento y,
   * sobre todo, la `transcript` con la que se pinta "Ver chat transcrito". Sin
   * rama de mock: con el mock activo la consulta falla y la vista degrada, que
   * es lo que ya hacía.
   */
  async getResultadoDeSesion(sessionId: string): Promise<RoleplaySessionResult> {
    const dto = await firstValueFrom(this.api.obtenerResultadoSesionRoleplay(sessionId));

    return {
      ...(this.aResultado(dto) as RoleplaySessionResult),
      ...this.cabeceraDesdeEvaluacion(dto),
    };
  }

  /**
   * La cabecera del intento, traducida de `evaluation` a las llaves planas de
   * `/end` que es lo que pintan las pantallas de resultados.
   *
   * Este endpoint agrupa la nota, la fecha y el QR bajo `evaluation` y con su
   * propia nomenclatura (`grade_scale`, `attempts_taken`), así que sin este
   * paso la cabecera de `roleplays/:id/results` se queda sin "Nota final", sin
   * sello y sin QR. Lo que ya venga plano manda: si backend empieza a mandarlo
   * con forma de `/end`, gana esa.
   *
   * Devuelve solo las llaves que tienen valor **a propósito**: el resultado se
   * mezcla con `fusionarResultado()`, y una llave presente en `undefined`
   * pisaría la buena que trae `base` en el flujo del tema.
   */
  private cabeceraDesdeEvaluacion(dto: RoleplaySessionResultsDto): Partial<RoleplayEndResponseDto> {
    const evaluacion = dto.evaluation;
    if (!evaluacion) return {};

    const validator =
      dto.validator ??
      evaluacion.validator ??
      // Sin QR pero con identificador todavía se puede sellar el intento.
      (evaluacion.identifier ? { image_qr: '', identifier: evaluacion.identifier } : undefined);

    const cabecera: Partial<RoleplayEndResponseDto> = {
      grade: dto.grade ?? evaluacion.grade,
      maximun_grade: dto.maximun_grade ?? evaluacion.grade_scale,
      evaluated_at: dto.evaluated_at ?? evaluacion.evaluated_at,
      validator,
      attempts_used: dto.attempts_used ?? evaluacion.attempts_taken,
      max_attempts: dto.max_attempts ?? evaluacion.max_attempts,
    };

    return this.sinVacias(cabecera);
  }

  /** Las llaves del objeto que sí tienen valor. Ver `cabeceraDesdeEvaluacion()`. */
  private sinVacias<T extends object>(objeto: T): Partial<T> {
    return Object.fromEntries(
      Object.entries(objeto).filter(([, valor]) => valor !== undefined && valor !== null)
    ) as Partial<T>;
  }

  /**
   * Completa el resultado que ya tiene la pantalla con el que devuelve
   * `getResultadoDeSesion()`.
   *
   * Los dos payloads se solapan pero ninguno es superconjunto del otro: `/end`
   * trae la cabecera del tema y las métricas de la sesión (`topic_name`,
   * `turns_used`, `max_turns`…) y el de la sesión trae la transcripción y el
   * bloque `evaluation`. Las llaves que el endpoint no manda ni siquiera
   * existen en su respuesta, así que el spread deja pasar las de `base` solo;
   * lo que va explícito es lo que `aResultado()` siempre define y podría llegar
   * vacío.
   */
  fusionarResultado(
    base: RoleplayResult | null,
    extra: RoleplaySessionResult
  ): RoleplaySessionResult {
    const tienePersonaje = !!(extra.personaje?.nombre || extra.personaje?.rol);

    return {
      ...(base ?? {}),
      ...extra,
      mensajes: extra.mensajes.length ? extra.mensajes : base?.mensajes ?? [],
      personaje: tienePersonaje ? extra.personaje : base?.personaje ?? extra.personaje,
      userRole: extra.userRole || base?.userRole || '',
    };
  }

  // ── modalidad streaming (conversación en vivo) ────────────────────────────────

  /**
   * Token efímero para abrir el canal WebRTC. Sin rama de mock a propósito: el
   * canal es real contra OpenAI, y un token inventado no negocia nada.
   *
   * Un 400 acá significa que el escenario no soporta streaming; quien llama
   * muestra el `detail` del error.
   */
  async getRealtimeToken(sessionId: string): Promise<RoleplayRealtimeTokenDto> {
    return firstValueFrom(this.api.obtenerTokenRealtimeRoleplay(sessionId));
  }

  /**
   * Registra un turno de la llamada en vivo. También sin mock: es la bitácora
   * que backend necesita para puntuar la sesión, y va contra el mismo
   * `session_id` que emitió el token.
   *
   * @param usage `response.usage` de OpenAI, serializado. Solo aplica al personaje.
   * @param audio voz del usuario en ese turno, grabada en local por
   *   `RoleplayStreamService` (OpenAI devuelve el texto pero no el audio, y la
   *   rúbrica lo necesita para la entonación). Solo aplica al trainee, y es
   *   opcional: si la grabación falló, el turno se reporta igual sin ella.
   */
  async reportRealtimeTurn(
    sessionId: string,
    role: RoleplayRealtimeRole,
    text: string,
    usage?: unknown,
    audio?: Blob | null
  ): Promise<RoleplayRealtimeTurnResponseDto> {
    const usageTexto = usage ? JSON.stringify(usage) : undefined;
    // Mismo nombre que en `sendTurn`: la extensión sale del mimeType real que
    // negoció el grabador, que en Safari/WKWebView no es webm.
    const fileName = audio
      ? `roleplay_${Date.now()}.${this.extensionDe(audio.type)}`
      : undefined;

    return firstValueFrom(
      this.api.reportarTurnoRealtimeRoleplay(
        sessionId,
        role,
        text,
        usageTexto,
        audio ?? undefined,
        fileName
      )
    );
  }

  // ── mapeo DTO → modelo de UI ───────────────────────────────────────────────

  /**
   * `persona.role_description` es el rol del personaje ("Gerente de compras
   * escéptico"), que la burbuja pinta junto a su nombre.
   */
  private aTopicConfig(dto: RoleplayTopicResponseDto): RoleplayTopicConfig {
    const escenario = dto.scenario;
    const persona = escenario?.persona;
    // Las reglas de rendición llegan anidadas, igual que en la teórica. Se
    // aplanan acá a propósito: la anidación no tiene por qué llegar a la vista.
    const ajustes = dto.evaluation_settings;

    return {
      escenario_id: escenario?.id ?? '',
      titulo: escenario?.title ?? '',
      contexto: escenario?.scenario_context ?? '',
      objetivos: escenario?.learning_objectives ?? [],
      dificultad: escenario?.difficulty ?? 'medium',
      personaje: {
        nombre: persona?.name ?? '',
        rol: persona?.role_description ?? '',
      },
      rasgos: persona?.personality_traits ?? [],
      modalidad: dto.student_mode,
      evaluable: !!dto.is_evaluable,
      modo_calificacion: dto.grade_mode,
      max_turns: escenario?.max_turns ?? 0,
      max_duration_seconds: escenario?.max_duration_seconds ?? 0,
      // Sin el bloque de backend el control de abandono queda apagado, que es
      // el comportamiento de antes de que existiera. Acá también entra el
      // roleplay libre: `getRoleplayLibreConfig()` mapea por esta misma vía.
      not_allowed_change_page: !!ajustes?.not_allowed_change_page,
      quantity_attempts_to_change_page: Number(ajustes?.quantity_attempts_to_change_page ?? 0),
    };
  }

  private aSesion(dto: RoleplayStartResponseDto, modoSolicitado: RoleplayMode): RoleplaySession {
    // El `mode` que confirma la API manda sobre el que pidió la página.
    const modo = dto.mode ?? modoSolicitado;
    // Respaldo: el personaje bueno llega por `getTopicConfig()`, y la página lo
    // prefiere. Esto solo cubre el rato en que esa llamada aún no respondió (o
    // falló) para que la burbuja no quede sin autor.
    const { personaje, user_role } = this.mock.getCharacter(modo);

    return {
      session_id: dto.session_id,
      mode: modo,
      personaje,
      user_role,
      // En `streaming` no hay saludo pregrabado: lo dice el personaje por el
      // canal en vivo (`RoleplayStreamService.pedirApertura()`), así que backend
      // manda `opening: null` y la conversación abre vacía.
      mensajes: dto.opening ? [this.mensajePersonaje(dto.opening.text, dto.opening.audio_url)] : [],
      interacciones_usadas: 0,
      max_turns: dto.max_turns,
      max_duration_seconds: dto.max_duration_seconds,
      finalizada: false,
      end_reason: null,
    };
  }

  private aTurno(dto: RoleplayTurnResponseDto, esMedia: boolean): RoleplayTurnResponse {
    const mensaje_usuario: RoleplayMessage = {
      id: this.uid(),
      autor: 'user',
      texto: dto.trainee_transcript,
      created_at: new Date().toISOString(),
      es_transcripcion: esMedia,
      estado: 'enviado',
    };

    return {
      mensaje_usuario,
      mensaje_personaje: dto.persona_text
        ? this.mensajePersonaje(dto.persona_text, dto.persona_audio_url)
        : null,
      interacciones_usadas: this.interaccionesDesde(dto.turn_index),
      finalizada: dto.session_ended,
      end_reason: dto.end_reason,
    };
  }

  /**
   * Un resultado en forma de UI, venga de `/end`, de los resultados del
   * assignment o de los de la sesión: los tres comparten cuerpo.
   *
   * Lo único que traduce es la transcripción: `role: 'persona' | 'trainee'` es
   * vocabulario de backend y las burbujas hablan de `autor: 'character' |
   * 'user'`. Si el payload no trae `transcript`, la pestaña del chat queda
   * vacía y el resto de la pantalla funciona igual.
   */
  private aResultado(dto: RoleplayEndResponseDto | RoleplaySessionResultsDto): RoleplayResult {
    return {
      ...dto,
      mensajes: this.aMensajes(dto.transcript),
      personaje: {
        nombre: dto.persona_name ?? '',
        rol: dto.persona_role_description ?? '',
      },
      userRole: dto.trainee_role ?? '',
    };
  }

  /**
   * La transcripción cerrada en burbujas.
   *
   * El `id` sale de `turn_index` porque tiene que ser único y estable: con él
   * la conversación hace `trackBy` y el reproductor decide qué burbuja suena
   * cuando dos comparten el audio.
   */
  private aMensajes(transcript?: RoleplayTranscriptTurnDto[]): RoleplayMessage[] {
    return (transcript ?? []).map((turno, indice) => ({
      id: `t${turno.turn_index ?? indice}`,
      autor: turno.role === 'trainee' ? 'user' : 'character',
      texto: turno.text,
      created_at: turno.created_at,
      es_transcripcion: !!turno.is_transcript,
      audio_url: turno.audio_url ?? undefined,
      estado: 'enviado',
    }));
  }

  /**
   * `turn_index` cuenta todos los mensajes (personaje + usuario), así que
   * avanza de dos en dos por intercambio: con `max_turns: 6` el cierre por
   * `turn_limit` llega con `turn_index: 12`. El contador del header muestra
   * interacciones del usuario, de ahí la división.
   */
  private interaccionesDesde(turnIndex: number): number {
    return Math.max(0, Math.floor((turnIndex ?? 0) / 2));
  }

  /**
   * Interacciones consumidas en la modalidad streaming.
   *
   * `realtime-turn` **no** cuenta como `turns` / `turns-text`: su `turn_index`
   * cuenta solo los turnos del usuario y es **0-based**, así que el turno 12 de
   * 12 llega como `turn_index: 11` con `end_reason: 'turn_limit'`. De ahí el +1,
   * y de ahí que no se divida entre 2 como en las otras modalidades.
   *
   * Devuelve 0 si el índice no es un número: con un `?? 0` a secas, un
   * `undefined` se contaría como una interacción de más.
   */
  interaccionesDesdeRealtime(turnIndex: number, maxTurns: number): number {
    if (!Number.isFinite(turnIndex)) return 0;
    const usadas = Math.max(0, Math.floor(turnIndex) + 1);
    return maxTurns > 0 ? Math.min(usadas, maxTurns) : usadas;
  }

  private mensajePersonaje(texto: string, audioUrl: string | null): RoleplayMessage {
    return {
      id: this.uid(),
      autor: 'character',
      texto,
      created_at: new Date().toISOString(),
      audio_url: audioUrl ?? undefined,
    };
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  /**
   * ¿Esta modalidad se sirve del mock?
   *
   * `streaming` **nunca**: su conversación ocurre contra OpenAI de verdad, y el
   * `session_id` con el que se pide el token efímero tiene que ser uno que
   * backend reconozca. Un id inventado por el mock devolvería 404 en
   * `realtime-token` y la llamada no abriría nunca.
   */
  private usarMock(modo: RoleplayMode): boolean {
    return this.USE_MOCK && modo !== 'streaming';
  }

  /** Ids que ya solo consume el mock: las APIs reales van con el body vacío. */
  private get ids(): RoleplayIds {
    const usuario = this.scormSession.getUserData();
    return {
      customer_id: this.CUSTOMER_ID,
      user_id: String(usuario?.id ?? localStorage.getItem('id_usuario') ?? ''),
      workspace_id: String(usuario?.workspace?.id ?? ''),
    };
  }

  private extensionDe(mimeType: string): string {
    if (!mimeType) return 'bin';
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('aac')) return 'aac';
    if (mimeType.includes('ogg')) return 'ogg';
    return 'bin';
  }

  private uid(): string {
    return Math.random().toString(36).slice(2, 11);
  }
}
